/**
 * CoachingUX launcher factory for Web SDK
 */
import { Api, ISurveyParams } from "@ms-ofb/floodgate-shared-typings";
import { SettingKey, getDynamicSetting } from "@ms-ofb/officepersonalization/dist/lib/Configuration/DynamicConfiguration";

import * as Configuration from "../Configuration/Configuration";
import * as Constants from "../Constants";
import * as Logging from "../Logging/Logging";
import * as AssetsProvider from "../TargetedMessaging/AssetsProvider";
import * as DomUtils from "../Window/DomUtils";
import * as Window from "../Window/Window";

import { IInitOptionsCommon } from "../Configuration/Configuration";
import { isNOU } from "../Utils";

export interface IBeaconParameters {
	actionName: string;
	actionUrl: string;
}

export interface IOfficeWebSurfaces {
	Initialize(intOptions: IInitOptionsCommon): void;
	RenderSurface(
		surfaceParams: any,
		targetElementId?: string,
		surfaceId?: string): void;
	RemoveSurface(): void;
	GetSurfaceContainerId(): string;
}

declare module "../Window/Window" {
	interface IWindow {
		OfficeWebSurfaces: IOfficeWebSurfaces;
	}
}

function getFirstCalendarDateOfWeek(input?: Date): Date {
	const inputDate = input ?? new Date();
    const day = inputDate.getDay();
	inputDate.setHours(-24 * day); 
	return inputDate;
}

// returns a checksum with year, month and first day of week - eg: 2023-4-16
export function getWeeklyDateStamp(inputDate?: Date): string {
	const firstDateOfweek = getFirstCalendarDateOfWeek(inputDate);
	return `${firstDateOfweek.getFullYear()}-${firstDateOfweek.getMonth()+1}-${firstDateOfweek.getDate()}`;
}

export function getWebSurfacesLink(timeStampDate?: Date) {
	const internalEnvs = ["dogfood", "edog", "int", "microsoft"];
	const commonInitOptions = Configuration.get().getCommonInitOptions();
	const audience = commonInitOptions.telemetryGroup?.audienceGroup || commonInitOptions.telemetryGroup?.audience;
	const isInternalEnv = audience && internalEnvs.indexOf(audience.toLowerCase()) !== -1;
	const webSurfacesConfigLink = isInternalEnv ?
		commonInitOptions.webSurfacesSettings?.webSurfacesIntUrl : commonInitOptions.webSurfacesSettings?.webSurfacesProdUrl;
	const webSurfacesDefaultLink = isInternalEnv ?
		Constants.DynamicScriptUrls.WebSurfacesIntLink : Constants.DynamicScriptUrls.WebSurfacesProdLink;
	const settingKey = isInternalEnv ? SettingKey.webSurfacesIntLink : SettingKey.webSurfacesProdLink;
	const webSurfacesLink = webSurfacesConfigLink || getDynamicSetting(settingKey, webSurfacesDefaultLink);

	const weekDateStamp = getWeeklyDateStamp(timeStampDate);
	return `${webSurfacesLink}?wds=${weekDateStamp}`;
}

// web surfaces script url load promise
let WebSurfacesScriptPromise: Promise<void>;
function createwebSurfacesScriptPromise() {
	if (!WebSurfacesScriptPromise) {
		WebSurfacesScriptPromise = DomUtils.loadScript(getWebSurfacesLink(), null);
	}
}

export default class GenericMessagingLauncherFactory implements Api.ISurveyLauncherFactory {
	public static readonly COACHINGUX_LAUNCHER_TYPE: string = "coachingux";
	public static readonly DIALOG_LAUNCHER_TYPE: string = "dialog";

	public static readonly SUPPORTED_SURVEY_TYPES: Api.SurveyType[] = [Api.SurveyType.GenericMessagingSurface, Api.SurveyType.Dialog];

	public static readonly SUPPORTED_LAUNCHER_TYPE: string[] = [
		GenericMessagingLauncherFactory.COACHINGUX_LAUNCHER_TYPE,
		GenericMessagingLauncherFactory.DIALOG_LAUNCHER_TYPE,
	];

	public makeSurveyLauncher(survey: Api.ISurvey): Api.ISurveyLauncher {
		if (this.AcceptsSurvey(survey)) {
			switch (survey.getLauncherType().toLocaleLowerCase()) {
				case GenericMessagingLauncherFactory.COACHINGUX_LAUNCHER_TYPE:
				case GenericMessagingLauncherFactory.DIALOG_LAUNCHER_TYPE:
					return new CoachingUXLauncher(survey);
				default:
					throw new Error("Unsupported launcher type: " + survey.getLauncherType());
			}
		}

		return null;
	}

	public AcceptsSurvey(survey: Api.ISurvey): boolean {
		if (!isNOU(survey) && GenericMessagingLauncherFactory.SUPPORTED_SURVEY_TYPES.includes(survey.getType()) &&
			GenericMessagingLauncherFactory.SUPPORTED_LAUNCHER_TYPE.includes(survey.getLauncherType().toLocaleLowerCase())) {
			return true;
		}

		return false;
	}
}

export class CoachingUXLauncher implements Api.ISurveyLauncher {
	private survey: Api.ISurvey;

	constructor(survey: Api.ISurvey) {
		this.survey = survey;

		// pre-load web surface package if there are any critical campaigns
		if (!WebSurfacesScriptPromise &&
			survey && survey.getSurveyInfo &&
			survey.getSurveyInfo()?.getBusinessLevel() === Api.SurveyInfoBusinessLevel.Critical) {
			setTimeout(createwebSurfacesScriptPromise, 1000);
		}
	}

	public launch(surveyParams?: ISurveyParams): void {

		if (this.survey.getLauncherType() != GenericMessagingLauncherFactory.COACHINGUX_LAUNCHER_TYPE) {
			throw new Error(`Invalid launcher type: '${this.survey.getLauncherType()}'. 
				Supported launcher type is: '${GenericMessagingLauncherFactory.COACHINGUX_LAUNCHER_TYPE}'.`);
		}

		const surveyInfo = this.survey && this.survey.getSurveyInfo();
		const surveyMetadata = surveyInfo && surveyInfo.getMetadata();
		const treatmentType = surveyInfo && surveyInfo.getTreatmentType();
		const contentMetadata = surveyMetadata && surveyMetadata.getContentMetadata();
		if (!contentMetadata) {
			return;
		}

		const startTime = new Date();
		const getWebSurfacesInstance = () => {
			const windowInstance = Window.getGlobal();
			return windowInstance && windowInstance.OfficeWebSurfaces;
		};

		const renderWebSurface = (uxInitOptions?: any) => {
			const officeWebSurfaces = getWebSurfacesInstance();
			if (uxInitOptions) {
				officeWebSurfaces.Initialize(uxInitOptions);
			}

			officeWebSurfaces.RemoveSurface();

			const surfaceParams = {
				...contentMetadata,
				surfaceContext: {
					surveyForm: this.survey,
					surveyParams,
				},
				treatmentType: treatmentType,
			};
			officeWebSurfaces.RenderSurface(
				surfaceParams,
				null /* targetElementId */,
				surveyInfo.getBackEndId());
		};

		const logErrorEvent = (error?: Error) => {
			Logging.getLogger().logEvent(
				Logging.EventIds.WebSurfaces.Common.Error.VALUE,
				Logging.LogLevel.Critical,
				{
					ErrorMessage: "Error loading websurfaces" + (error && error.message ? ": " + error.message : ""),
					TimeMilliseconds: Date.now() - (+startTime),
				});
		};

		try {
			if (getWebSurfacesInstance()) {
				renderWebSurface();
				return;
			}

			createwebSurfacesScriptPromise();

			// if UX package isn't already loaded, download package and render surface
			WebSurfacesScriptPromise
				.then(() => {
					const initOptions = Configuration.get().getCommonInitOptions();
					const floodgateInitOptions = Configuration.get().getFloodgateInitOptions();
					const surveyActivatedCallback = floodgateInitOptions && floodgateInitOptions.onSurveyActivatedCallback;

					const beaconCallback = (beaconParameters: IBeaconParameters): Promise<boolean> => {
						if (beaconParameters) {
							return AssetsProvider.get().setCampaignAction(beaconParameters);
						}
					};

					const uxOptions = {
						...initOptions,
						...floodgateInitOptions,
						beaconCallback,
						surfaceInitTime: startTime,
						surfaceId: surveyInfo.getBackEndId(),
						logSessionId: Configuration.get().getSdkSessionId(),
						logSettings: getDynamicSetting(SettingKey.logLevelSettings, undefined),
						renderer: surveyActivatedCallback,
						renderCallback: surveyActivatedCallback && surveyActivatedCallback.onTeachingCampaignRender,
					};

					renderWebSurface(uxOptions);
				}).catch(error => {
					logErrorEvent(error);
				});
		} catch (error) {
			logErrorEvent(error);
		}
	}
}
